import { useEffect, useMemo, useState } from "react";

import CustomBox from "../../../../../Components/UI/CustomBox/CustomBox/CustomBox";
import ExpandOrShrinkIcon from "../../../../../Components/UI/DropDown/ExpandOrShrinkIcon";
import DetailButton from "../DetectionDetailPopup/DetailButton";
import useDetectionDetailPopup from "../DetectionDetailPopup/useDetectionDetailPopup";

import "./styles.scss";

const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 200;
const CONTEXT_WINDOW = 90;

// Keys that carry no analyst value in search results
const SKIP_KEYS = new Set(["_id", "fileID", "file_cpuid"]);
// Mongo extended-json wrappers: use their value but keep the parent key in the path
const WRAPPER_KEYS = new Set(["$oid", "$date", "$numberLong", "$numberInt", "$numberDouble"]);

const SECTION_LABELS = {
  verdict: "Verdict",
  decision_path: "Verdict",
  activity_name: "Verdict",
  verdict_description: "Verdict",
  confidence: "Verdict",
  risk_score: "Verdict",
  threat_counts: "Threat Counts",
  threat_details_by_severity: "Threat Details",
  top_risky_files: "Top Risky Files",
  top_risky_memory_processes: "Top Risky Processes",
  analysis_details: "Analysis Details",
  detection_details: "Detection Details",
  enriched_malicious_hashes: "Enriched Threat Data",
  suspicious_processes: "Suspicious Processes",
  system_info: "System Info",
  network_connections: "Network Connections",
  top_threat_categories: "Threat Categories",
  active_threats: "Active Threats",
  raw_report: "System Report",
  filename: "General",
  original_id: "General",
  clientID: "General",
  report_time: "General",
};

const prettifyKey = (key = "") =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const sectionLabel = (rootKey) => SECTION_LABELS[rootKey] || prettifyKey(rootKey);

const normalizePath = (path) =>
  path
    ? String(path)
        .trim()
        .toLowerCase()
        .replace(/[\\/]+/g, "\\")
    : "";

const searchReport = (report, rawQuery) => {
  const results = [];
  const query = rawQuery.toLowerCase();
  // Path-like queries must also match across \\ vs \ and case differences
  const normalizedQuery = /[\\/]/.test(rawQuery) ? normalizePath(rawQuery) : "";

  const matchesQuery = (text) => {
    const lower = text.toLowerCase();
    if (lower.includes(query)) return true;
    return normalizedQuery ? normalizePath(text).includes(normalizedQuery) : false;
  };

  const visit = (node, path, parent) => {
    if (results.length >= RESULT_LIMIT || node === null || node === undefined) return;

    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        if (results.length >= RESULT_LIMIT) return;
        visit(node[i], [...path, `#${i + 1}`], node);
      }
      return;
    }

    if (typeof node === "object") {
      for (const [key, value] of Object.entries(node)) {
        if (results.length >= RESULT_LIMIT) return;
        if (SKIP_KEYS.has(key)) continue;
        if (WRAPPER_KEYS.has(key)) {
          visit(value, path, parent);
          continue;
        }

        // Field-name matches surface primitive values under matching keys
        const isPrimitive = value === null || typeof value !== "object";
        if (isPrimitive && key.toLowerCase().includes(query)) {
          const text = value === null || value === undefined ? "" : String(value);
          results.push({ path: [...path, key], value: text, matchedOn: "field", parent: node });
          continue;
        }

        visit(value, [...path, key], node);
      }
      return;
    }

    const text = String(node);
    if (matchesQuery(text)) {
      results.push({ path, value: text, matchedOn: "value", parent });
    }
  };

  visit(report, [], null);
  return results;
};

// Trim long values to a window around the first match so the hit stays visible
const contextTrim = (text, query) => {
  if (text.length <= CONTEXT_WINDOW * 2) return text;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return `${text.substring(0, CONTEXT_WINDOW * 2)}...`;
  const start = Math.max(0, index - CONTEXT_WINDOW);
  const end = Math.min(text.length, index + query.length + CONTEXT_WINDOW);
  return `${start > 0 ? "..." : ""}${text.substring(start, end)}${end < text.length ? "..." : ""}`;
};

const MAX_JSON_LENGTH = 20000;

// The whole record the matched node belongs to, pretty-printed
const relatedJson = (result) => {
  const related =
    result.path.length > 1 && result.parent
      ? result.parent
      : { [result.path[result.path.length - 1] || "value"]: result.value };
  const json = JSON.stringify(related, null, 2);
  return json.length > MAX_JSON_LENGTH
    ? `${json.substring(0, MAX_JSON_LENGTH)}\n... (truncated)`
    : json;
};

const HighlightedText = ({ text, query }) => {
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part,
  );
};

const ReportSearch = ({ report }) => {
  const { findFileDetail, findProcessDetail, openDetail, popup } = useDetectionDetailPopup();

  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  // Debounce typing before running the search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue.trim());
      setSelectedSection(null);
      setExpandedRows({});
    }, 300);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const toggleRow = (index) => setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));

  const results = useMemo(() => {
    if (!report || query.length < MIN_QUERY_LENGTH) return [];
    return searchReport(report, query);
  }, [report, query]);

  const sections = useMemo(() => {
    const counts = new Map();
    results.forEach((result) => {
      const label = sectionLabel(result.path[0]);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [...counts.entries()];
  }, [results]);

  const visibleResults = selectedSection
    ? results.filter((result) => sectionLabel(result.path[0]) === selectedSection)
    : results;

  const findResultDetail = (result) => {
    if (result.value.length > 300) return null;
    return findFileDetail(result.value) || findProcessDetail({ process: result.value });
  };

  return (
    <CustomBox className="thunter-report-search">
      <div className="thunter-report-search__bar">
        <input
          className="thunter-report-search__input"
          placeholder="Search anything in this report: file paths, processes, rules, hashes, hosts..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <p className="thunter-report-search__summary">
          {query.length < MIN_QUERY_LENGTH
            ? `Type at least ${MIN_QUERY_LENGTH} characters to search the whole report.`
            : `${results.length}${results.length >= RESULT_LIMIT ? "+" : ""} ${
                results.length === 1 ? "match" : "matches"
              }`}
        </p>
      </div>

      {sections.length > 1 ? (
        <div className="thunter-report-search__sections">
          <button
            type="button"
            className={`thunter-report-search__section-chip ${
              selectedSection === null ? "thunter-report-search__section-chip--active" : ""
            }`}
            onClick={() => {
              setSelectedSection(null);
              setExpandedRows({});
            }}>
            All ({results.length})
          </button>
          {sections.map(([label, count]) => (
            <button
              key={label}
              type="button"
              className={`thunter-report-search__section-chip ${
                selectedSection === label ? "thunter-report-search__section-chip--active" : ""
              }`}
              onClick={() => {
                setSelectedSection(selectedSection === label ? null : label);
                setExpandedRows({});
              }}>
              {label} ({count})
            </button>
          ))}
        </div>
      ) : null}

      {query.length >= MIN_QUERY_LENGTH ? (
        visibleResults.length > 0 ? (
          <div className="thunter-report-search__results">
            {visibleResults.map((result, index) => {
              const resultDetail = findResultDetail(result);
              const expanded = !!expandedRows[index];

              return (
                <div className="thunter-report-search__result" key={index}>
                  <div className="thunter-report-search__result-path">
                    <span className="thunter-report-search__result-section">
                      {sectionLabel(result.path[0])}
                    </span>
                    {result.path.slice(1).map((part, i) => (
                      <span key={i} className="thunter-report-search__result-crumb">
                        {" › "}
                        {part.startsWith("#") ? part : prettifyKey(part)}
                      </span>
                    ))}
                  </div>
                  <div className="thunter-report-search__result-value">
                    <HighlightedText text={contextTrim(result.value, query)} query={query} />
                    <button
                      type="button"
                      className="thunter-detail-button"
                      title={expanded ? "Hide related data" : "Show whole related data"}
                      onClick={() => toggleRow(index)}>
                      <ExpandOrShrinkIcon expanded={expanded} collapsed={false} />
                    </button>
                    {resultDetail ? (
                      <DetailButton
                        title="Show file/process details"
                        onClick={() => openDetail(resultDetail)}
                      />
                    ) : null}
                  </div>
                  {expanded ? (
                    <pre className="thunter-report-search__result-json">{relatedJson(result)}</pre>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="thunter-report-search__no-results">
            No matches found for "{query}" in this report.
          </p>
        )
      ) : null}

      {popup}
    </CustomBox>
  );
};

export default ReportSearch;
